<template>
  <div class="container">
    <!-- Route Details Section -->
    <div class="section-card">
      <div class="section-header-row">
        <h4 class="section-title mb-0">1. Route Details</h4>
      </div>
      <div class="row mt-3">
        <div class="col-md-6 form-group">
          <label>Route Name <span class="text-danger">*</span></label>
          <input type="text" v-model="route.name" class="form-control" placeholder="Enter route name" />
        </div>
        <div class="col-md-6 form-group">
          <label>Distance (Km) <span class="text-danger">*</span></label>
          <input type="number" v-model="route.distance" class="form-control" placeholder="0.0" />
        </div>
        <div class="col-md-6 form-group">
          <label>Start Point <span class="text-danger">*</span></label>
          <input type="text" v-model="route.startPoint" class="form-control" placeholder="Start location" />
        </div>
        <div class="col-md-6 form-group">
          <label>End Point <span class="text-danger">*</span></label>
          <input type="text" v-model="route.endPoint" class="form-control" placeholder="End location" />
        </div>
      </div>
    </div>

    <!-- Swap Dealer Section -->
    <div class="section-card">
      <div class="section-header-row">
        <h4 class="section-title mb-0">2. Swap Dealer (Re-assign)</h4>
        <div class="header-actions">
          <div class="search-wrapper" v-if="isSwapOpen && !loadingDealers">
            <input 
              type="text" 
              v-model="searchSwapQuery" 
              placeholder="Search dealers..." 
              class="header-search-input"
              @click.stop
            />
            <i class="fa fa-search search-icon"></i>
          </div>

          <i 
            class="fa toggle-icon" 
            :class="isSwapOpen ? 'fa-angle-up' : 'fa-angle-down'" 
            @click="isSwapOpen = !isSwapOpen"
            :title="isSwapOpen ? 'Collapse' : 'Expand'"
          ></i>
        </div>
      </div>
      
      <div v-show="isSwapOpen" class="table-responsive mt-3">
        <div v-if="loadingDealers" class="text-center py-3 text-muted">
          <i class="fa fa-spinner fa-spin"></i> Loading dealers...
        </div>
        
        <table v-else class="table table-responsive">
          <thead>
            <tr>
              <th>Dealer Name</th>
              <th>Address</th>
              <th>City</th>
              <th>Existing Route</th>
              <th class="text-center danger-zone">Action (Danger Zone)</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="dealer in filteredSwapList" :key="dealer.id">
              <td :title="dealer?.business_name">{{ truncateText(dealer.business_name, 20) }}</td>
              <td :title="dealer?.address">{{ truncateText(dealer.address, 25) }}</td>
              <td :title="dealer?.city">{{ truncateText(dealer.city, 15) }}</td>
              <td :title="dealer?.route_name"><span class="badge badge-info">{{ truncateText(dealer.route_name, 20) }}</span></td>
              <td class="text-center bg-light-danger">
                <button 
                  type="button" 
                  class="btn btn-sm"
                  :class="isSwapped(dealer.id) ? 'btn-danger' : 'btn-success'"
                  @click="toggleSwap(dealer.id)"
                >
                  {{ isSwapped(dealer.id) ? 'Unswap Route' : 'Swap Route' }}
                </button>
              </td>
            </tr>
            <tr v-if="!loadingDealers && filteredSwapList.length === 0">
              <td colspan="5" class="text-center">
                {{ searchSwapQuery ? 'No dealers found matching search.' : 'No assignable dealers found for this distributor.' }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Map Dealer Section -->
    <div class="section-card">
      <div class="section-header-row">
        <h4 class="section-title mb-0">3. Map Dealer (Unassigned)</h4>
        
        <div class="header-actions">
          <div class="search-wrapper" v-if="isMapOpen && !loadingDealers">
            <input 
              type="text" 
              v-model="searchMapQuery" 
              placeholder="Search dealers..." 
              class="header-search-input"
              @click.stop
            />
            <i class="fa fa-search search-icon"></i>
          </div>

          <i 
            class="fa toggle-icon" 
            :class="isMapOpen ? 'fa-angle-up' : 'fa-angle-down'" 
            @click="isMapOpen = !isMapOpen"
            :title="isMapOpen ? 'Collapse' : 'Expand'"
          ></i>
        </div>
      </div>

      <div v-show="isMapOpen" class="table-responsive mt-3">
        <div v-if="loadingDealers" class="text-center py-3 text-muted">
          <i class="fa fa-spinner fa-spin"></i> Loading dealers...
        </div>

        <table v-else class="table table-responsive">
          <thead>
            <tr>
              <th>Dealer Name</th>
              <th>Address</th>
              <th>City</th>
              <th class="text-center">Map (Select)</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="dealer in filteredMapList" :key="dealer.dealer_profile_id">
              <td :title="dealer?.business_name">{{ truncateText(dealer.business_name, 20) }}</td>
              <td :title="dealer?.address">{{ truncateText(dealer.address, 25) }}</td>
              <td :title="dealer?.city">{{ truncateText(dealer.city, 15) }}</td>
              <td class="text-center">
                <input 
                  type="checkbox" 
                  class="big-checkbox"
                  :checked="isMapped(dealer.dealer_profile_id)"
                  @change="toggleMap(dealer.dealer_profile_id)"
                />
              </td>
            </tr>
            <tr v-if="!loadingDealers && filteredMapList.length === 0">
              <td colspan="4" class="text-center">
                {{ searchMapQuery ? 'No dealers found matching search.' : 'No unassigned dealers found.' }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Form Actions -->
    <div class="form-actions">
      <button class="btn btn-danger" @click="cancel">Cancel</button>
      <button class="btn btn-success" @click="saveRoute" :disabled="loading">
        <span v-if="loading">
          <span class="spinner"></span>
        </span>
        <span v-else>
          Save Route
        </span>
      </button>
    </div>
  </div>
</template>

<script>
import { useToast } from "vue-toastification";
import { getDealersList, createNewRoute } from "@/services/dataService"; 

export default {
  name: "CreateRoute",
  setup() {
    const toast = useToast();
    return { toast };
  },
  data() {
    return {
      token: localStorage.getItem("token_set") || "",
      profileId: localStorage.getItem("profile_id") || "",
      reportingProfileId: localStorage.getItem("reportingL1ProfileId") || "",
      userCategory: localStorage.getItem("user_label") || "",
      loadingDealers: false,
      loading: false,
      isSwapOpen: true,
      isMapOpen: true,
      
      searchSwapQuery: "",
      searchMapQuery: "",

      route: {
        name: '',
        startPoint: '',
        endPoint: '',
        distance: '',
      },

      selectedSwapIds: [], 
      selectedMapIds: [],  

      // Empty by default, populated via API
      swapDealersList: [],
      mapDealersList: []
    };
  },
  mounted() {
    this.fetchDealers();
  },
  computed: {
    filteredSwapList() {
      if (!this.searchSwapQuery) return this.swapDealersList;
      const query = this.searchSwapQuery.toLowerCase();
      return this.swapDealersList.filter(dealer => 
        (dealer.business_name || '').toLowerCase().includes(query) ||
        (dealer.city || '').toLowerCase().includes(query)
      );
    },
    filteredMapList() {
      if (!this.searchMapQuery) return this.mapDealersList;
      const query = this.searchMapQuery.toLowerCase();
      return this.mapDealersList.filter(dealer => 
        (dealer.business_name || '').toLowerCase().includes(query) ||
        (dealer.city || '').toLowerCase().includes(query)
      );
    }
  },
  methods: {
    // Helper to truncate long strings
    truncateText(text, length) {
      if (!text) return '';
      if (text.length <= length) return text;
      return text.substring(0, length) + '...';
    },

    // Fetch dealers list from API
    async fetchDealers() {
      this.loadingDealers = true;
      this.selectedSwapIds = [];
      this.selectedMapIds = []; 

      try {
        let idToUse = this.profileId;
        if (this.userCategory === 'DISTRIBUTOR BACK OFFICE') {
          idToUse = this.reportingProfileId;
        }

        // Calling the API with distributor ID
        const response = await getDealersList(this.token, idToUse);
        
        if (response.data && response.data.status === 'success' && response.data.data.assignedDealers && response.data.data.unassignedDealers) {
          // Map API response to local data
          this.swapDealersList = response.data.data.assignedDealers || [];
          this.mapDealersList = response.data.data.unassignedDealers || [];
        } else {
          this.toast.error("Failed to load dealers.");
        }
      } catch (error) {
        console.error("Error fetching dealers:", error);
        this.toast.error("Error loading dealers list.");
      } finally {
        this.loadingDealers = false;
      }
    },

    // Swap Logic (Uses dealer.id - the mapping ID)
    isSwapped(id) {
      return this.selectedSwapIds.includes(id);
    },
    toggleSwap(id) {
      if (this.isSwapped(id)) {
        this.selectedSwapIds = this.selectedSwapIds.filter(item => item !== id);
      } else {
        this.selectedSwapIds.push(id);
      }
    },

    // Map Logic (Uses dealer.dealer_profile_id)
    isMapped(id) {
      return this.selectedMapIds.includes(id);
    },
    toggleMap(id) {
      if (this.isMapped(id)) {
        this.selectedMapIds = this.selectedMapIds.filter(item => item !== id);
      } else {
        this.selectedMapIds.push(id);
      }
    },

    cancel() {
      this.$router.back(); 
    },

    validateForm() {
      if (!this.route.name || this.route.name.trim() === '') {
        this.toast.error("Route name is required.");
        return false;
      }
      if (!this.route.distance) {
        this.toast.error("Distance is required."); 
        return false;
      }
      if (!this.route.startPoint || this.route.startPoint.trim() === '') {
        this.toast.error("Start point is required.");
        return false;
      }
      if (!this.route.endPoint || this.route.endPoint.trim() === '') {
        this.toast.error("End point is required.");
        return false;
      }
      return true;
    },

    async saveRoute() {
      if (!this.validateForm()) return;

      this.loading = true;

      const payload = {
        "route_name": this.route.name,
        "start_point": this.route.startPoint,
        "distance": this.route.distance,
        "end_point": this.route.endPoint,
        "distributor_id": this.userCategory === 'DISTRIBUTOR BACK OFFICE' ? this.reportingProfileId : this.profileId,
        "created_by": this.profileId,
        "swap-dealer-route": this.selectedSwapIds,
        "map-dealer-route": this.selectedMapIds
      };

      console.log("Final Payload:", JSON.stringify(payload, null, 2));

      try {
        // Call the API
        const response = await createNewRoute(this.token, payload);
        if(response.data && response.data.status === 'success') {
          this.toast.success(response.data.message);
          this.$router.push('/routes');
        } else {
          this.toast.error(response.data.message);
        }
      } catch(error) {
          this.toast.error("An error occurred while saving.");
      } finally {
          this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
html, body {
  height: 100%;
  margin: 0;
  padding: 0;
  background-color: #f0f0f0;
}

.container {
  background-color: #ffffff;
  padding: 20px 15px;
  margin: 10px auto;
  width: 100%;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  box-sizing: border-box;
  max-width: 100%;
}

.section-card {
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 15px;
  margin-bottom: 20px;
  background-color: #fff;
}

.section-header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 10px;
  margin-bottom: 10px;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  text-transform: uppercase;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 15px; 
}

.search-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.header-search-input {
  padding: 5px 30px 5px 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 13px;
  width: 200px;
  transition: border-color 0.3s;
}

.header-search-input:focus {
  border-color: #007bff;
  outline: none;
}

.search-icon {
  position: absolute;
  right: 10px;
  color: #999;
  font-size: 13px;
  pointer-events: none;
}

.toggle-icon {
  cursor: pointer;
  color: #6c757d;
  font-size: 1.1rem;
  transition: color 0.3s ease;
  padding: 5px;
}

.toggle-icon:hover { 
  color: #007bff; 
  transform: scale(1.1); 
}

.mb-0 { 
  margin-bottom: 0 !important; 
}

.mt-3 { 
  margin-top: 1rem !important; 
}

.row {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
}

.col-md-6 {
  flex: 1 1 45%; 
  min-width: 300px;
}

.form-group { 
  margin-bottom: 10px; 
}

label {
  display: block;
  font-weight: 500;
  margin-bottom: 5px;
  color: #555;
}

.form-control {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ccc;
  border-radius: 4px;
  box-sizing: border-box; 
}

select.form-control {
  appearance: auto;
}

.text-danger { 
  color: #dc3545; 
}

.table-responsive { 
  overflow-x: auto; 
}

.table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 10px;
}

.table th, .table td {
  padding: 10px;
  border: 1px solid #ddd;
  text-align: left;
}

.table th { 
  background-color: #f8f9fa; 
  font-weight: 600; 
  white-space: nowrap;
}

.text-center { 
  text-align: center !important; 
}

.badge-info {
  background-color: #17a2b8;
  color: white;
  padding: 3px 8px;
  border-radius: 10px;
  font-size: 12px;
}

.danger-zone {
  background-color: #f8d7da !important;
  color: #721c24;
  border-color: #f5c6cb;
}

.bg-light-danger { 
  background-color: #fff5f5; 
}

.btn {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.btn-sm { 
  padding: 5px 10px; 
  font-size: 12px; 
}

.btn-success { 
  background-color: #28a745; 
  color: white; 
}

.btn-success:hover { 
  background-color: #218838; 
}

.btn-danger { 
  background-color: #dc3545; 
  color: white; 
}

.btn-danger:hover { 
  background-color: #c82333; 
}

.btn-secondary { 
  background-color: #6c757d; 
  color: white; 
}

.btn-secondary:hover { 
  background-color: #5a6268; 
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
  padding-top: 20px;
}

.spinner {
  border: 3px solid #f3f3f3;
  border-top: 3px solid #54ba4a;
  border-radius: 50%;
  width: 16px;
  height: 16px;
  animation: spin 1s linear infinite;
  display: inline-block;
  vertical-align: middle;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.big-checkbox {
  width: 20px;
  height: 20px;
  cursor: pointer;
}

@media (max-width: 600px) {
  .header-actions {
    margin-top: 10px;
    width: 100%;
    justify-content: space-between;
  }

  .section-header-row { 
    flex-wrap: wrap; 
  }

  .header-search-input { 
    width: 85%; 
  }
}
</style>